import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';

export const PERIOD_NA = {
  SELECT_MONTH_COUNT: 18,
  RECENT_7_DAYS: 7,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

export const HISTORY_FILTER_PERIOD_NA = {
  SELECT_MONTH: 'select-month',
  RECENT_7_DAYS: 'recent-7-days',
  RECENT_30_DAYS: 'recent-30-days',
  CUSTOM_DAYS: 'custom-days',
} as const;

export type HistoryFilterPeriodNa = (typeof HISTORY_FILTER_PERIOD_NA)[keyof typeof HISTORY_FILTER_PERIOD_NA];

export interface HistoryFilterFormStateNa {
  period: HistoryFilterPeriodNa;
  startDate: Date | null;
  endDate: Date | null;
  tagNames: Array<string>;
  driverNames?: Array<string>;
}

export interface HistoryFilterQueryStateNa extends HistoryFilterFormStateNa {
  sourceDate: Date | null;
}

function isValidPeriod(period: string | null): period is HistoryFilterPeriodNa {
  return !!period && Object.values(HISTORY_FILTER_PERIOD_NA).includes(period as HistoryFilterPeriodNa);
}

export function getHistoryFilterPeriodState(period: HistoryFilterPeriodNa, today = new Date()) {
  switch (period) {
    case HISTORY_FILTER_PERIOD_NA.SELECT_MONTH:
      // 실제 날짜는 SelectMonthBottomSheet에서 고른 월로 호출부에서 직접 세팅함
      return {period, startDate: null, endDate: null};
    case HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD_NA.RECENT_7_DAYS - 1}),
        endDate: today,
      };
    case HISTORY_FILTER_PERIOD_NA.RECENT_30_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD_NA.RECENT_30_DAYS - 1}),
        endDate: today,
      };
    case HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS:
      return {
        period,
        startDate: null,
        endDate: null,
      };
  }
}

export function getDefaultHistoryFilterState(today = new Date()): HistoryFilterQueryStateNa {
  const defaultPeriodState = getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS, today);

  return {
    ...defaultPeriodState,
    sourceDate: null,
    tagNames: [],
    driverNames: [],
  };
}

export function parseHistoryFilterSearchParams(searchParams: URLSearchParams, today = new Date()): HistoryFilterQueryStateNa {
  const defaultState = getDefaultHistoryFilterState(today);
  const period = searchParams.get('period');
  const validatedPeriod = isValidPeriod(period) ? period : defaultState.period;
  const periodState = getHistoryFilterPeriodState(validatedPeriod, today);
  const startDate = parseDateWithFormat(searchParams.get('startDate')) || periodState.startDate;
  const endDate = parseDateWithFormat(searchParams.get('endDate')) || periodState.endDate;

  return {
    sourceDate: parseDateWithFormat(searchParams.get('sourceDate')),
    period: validatedPeriod,
    startDate,
    endDate,
    tagNames: searchParams.getAll('tag').filter(Boolean),
    driverNames: searchParams.getAll('driver').filter(Boolean),
  };
}

export function createHistoryFilterSearchParams(state: HistoryFilterQueryStateNa) {
  const params = new URLSearchParams();

  params.set('period', state.period);

  if (state.sourceDate) {
    params.set('sourceDate', getYYYYMMddFormat(state.sourceDate));
  }

  if (state.startDate) {
    params.set('startDate', getYYYYMMddFormat(state.startDate));
  }

  if (state.endDate) {
    params.set('endDate', getYYYYMMddFormat(state.endDate));
  }

  state.tagNames.forEach((tagName) => {
    params.append('tag', tagName);
  });

  (state.driverNames ?? []).forEach((driverName) => {
    params.append('driver', driverName);
  });

  return params;
}

export function createHistoryFilterSearch(state: HistoryFilterQueryStateNa) {
  return `?${createHistoryFilterSearchParams(state).toString()}`;
}

export function getHistoryFilterFormState(state: HistoryFilterQueryStateNa): HistoryFilterFormStateNa {
  return {
    period: state.period,
    startDate: state.startDate,
    endDate: state.endDate,
    tagNames: state.tagNames,
    driverNames: state.driverNames,
  };
}
